/** Human support chat system types */

export type SupportRequestStatus =
  | 'waiting'
  | 'accepted'
  | 'rejected'
  | 'closed'
  | 'cancelled'
  | 'info_needed';

export type AgentRole = 'agent' | 'lead' | 'admin';

export type AgentPresenceStatus = 'online' | 'offline' | 'busy' | 'idle';

export type MessageSenderRole = 'customer' | 'agent' | 'system';

export type ChatMode = 'ai' | 'waiting' | 'human' | 'ended';

export type Priority = 'low' | 'normal' | 'high' | 'urgent';

export interface AiHistoryItem {
  role: 'user' | 'assistant';
  content: string;
}

export interface BrowserInfo {
  userAgent: string;
  language: string;
  platform: string;
  screenWidth: number;
  screenHeight: number;
  timezone: string;
}

export interface SupportRequest {
  requestId: string;
  customerId: string;
  customerName: string | null;
  anonymousUserId: string;
  browserInfo: BrowserInfo;
  pageUrl: string;
  createdAt: number;
  status: SupportRequestStatus;
  assignedAgent: string | null;
  acceptedAt?: number | null;
  closedAt?: number | null;
  aiHistory: AiHistoryItem[];
  issueSummary: string;
  priority: Priority;
  source: 'chatbot';
  queuePosition?: number;
  estimatedWaitSec?: number;
  rejectedBy?: string | null;
  transferNote?: string | null;
}

export interface SupportRequestSecret {
  accessToken: string;
  createdAt: number;
}

export interface WorkingHours {
  start: string; // "09:00"
  end: string; // "18:00"
  timezone: string;
  days: number[]; // 0=Sun .. 6=Sat
}

export interface SupportAgent {
  agentId: string;
  name: string;
  email: string;
  role: AgentRole;
  active: boolean;
  profileImage: string | null;
  phone: string | null;
  workingHours: WorkingHours | null;
  maxConcurrentChats: number;
  presenceStatus: AgentPresenceStatus;
  createdAt: number;
  updatedAt: number;
}

export interface ActiveChat {
  requestId: string;
  customerId: string;
  agentId: string;
  startedAt: number;
  status: 'active' | 'closed';
  lastMessageAt: number;
  unreadCustomer: number;
  unreadAgent: number;
}

export interface ChatMessage {
  messageId: string;
  senderId: string;
  senderRole: MessageSenderRole;
  text: string;
  createdAt: number;
  delivered: boolean;
  read: boolean;
  type: 'text' | 'system';
}

export interface ChatHistoryRecord {
  requestId: string;
  customerId: string;
  customerName: string | null;
  agentId: string;
  agentName: string;
  startedAt: number;
  endedAt: number;
  durationSec: number;
  messages: ChatMessage[];
  status: 'closed' | 'transferred';
  issueSummary: string;
  pageUrl: string;
}

export interface PresenceRecord {
  uid: string;
  online: boolean;
  lastSeen: number;
  role: 'customer' | 'agent' | 'admin';
  status?: AgentPresenceStatus;
  pageUrl?: string;
  requestId?: string | null;
}

export interface TypingRecord {
  uid: string;
  isTyping: boolean;
  updatedAt: number;
}

export interface SupportNotification {
  id: string;
  type: 'new_request' | 'new_message' | 'request_accepted' | 'request_closed' | 'transfer';
  title: string;
  body: string;
  requestId?: string;
  createdAt: number;
  read: boolean;
}

export interface EmailQueueItem {
  id: string;
  to: string;
  agentId: string;
  requestId: string;
  template: 'support_request';
  payload: Record<string, string>;
  createdAt: number;
  status: 'pending' | 'sent' | 'failed';
}

export interface SupportSettings {
  requestTimeoutMs: number;
  estimatedWaitPerPositionSec: number;
  welcomeMessage: string;
}

export const DEFAULT_SUPPORT_SETTINGS: SupportSettings = {
  requestTimeoutMs: 30 * 60 * 1000,
  estimatedWaitPerPositionSec: 120,
  welcomeMessage: 'A support agent will be with you shortly.',
};

export const SUPPORT_INTENT_PATTERN =
  /\b(connect\s+(me\s+)?(to|with)\s+(an?\s+)?(support|human|agent|expert|executive)|talk\s+to\s+(a\s+)?(human|support|agent|expert|executive|customer\s*(care|support))|live\s+agent|customer\s*(care|support)|human\s+(assistant|support|agent)|support\s+executive|speak\s+(to|with)\s+(a\s+)?(human|agent|person|expert)|real\s+(person|human|agent)|need\s+(a\s+)?(human|agent|expert)|transfer\s+(me\s+)?to\s+(a\s+)?(human|agent|expert))\b/i;
